import React, {
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    IconButton,
    Typography,
    InputLabel,
} from '@mui/material';
import {
    AppFormikTextField,
    AppPrimaryButton,
    AppSecondaryButton,
} from 'shared/components/AppComponents';
import CloseIcon from '@mui/icons-material/Close';
import { Toast, Utils } from 'shared/helpers';
import { TemplateService } from 'services';
import { connect } from 'react-redux';
import { ProfileState } from 'state/profile/Reducer';
import { Formik, FormikProps } from 'formik';
import { AxiosError } from 'axios';
// import { shareFormScheme } from 'validators/ShareTemplateScheme';

export interface ShareTemplateFormProps {
    showForm: boolean;
    onCloseForm: () => void;
    profileState: ProfileState;
}

interface MemberListProps {
    email: string;
    roleId: string;
    name: string;
    userId: string;
}

interface FormikPropsValues {
    email: string;
}
const ShareTemplateFormDialog = (
    props: ShareTemplateFormProps
): ReactElement => {
    const innerSubmitRef = useRef<HTMLButtonElement | null>(null);
    const { showForm, onCloseForm, profileState } = props;
    const formikRef = useRef<FormikProps<FormikPropsValues> | null>(null);
    const [members, setMembers] = useState<MemberListProps[]>([]);
    const [isLoadingMembers, setLoadingMembers] = useState(false);
    const [isSharing, setSharing] = useState(false);
    // const [isSharedMember, setSharedMember] = useState<boolean>(false);
    const pagination = {
        offset: 1,
        limit: 100,
    };

    // API Calls
    const loadMembersList = (): void => {
        setLoadingMembers(true);
        TemplateService.getMembers(pagination.offset, pagination.limit)
            .then((response: any) => {
                const { paginatedResult } = response.data.data[0];
                const memberList: MemberListProps[] = [];
                paginatedResult.forEach((m: any) => {
                    const { email, roleId, userId, name } = m;
                    // eslint-disable-next-line no-underscore-dangle
                    if (profileState.userInfo._id !== m.userId) {
                        const processedList = {
                            email,
                            roleId,
                            userId,
                            name: `${name.first} ${name.last}`,
                        };
                        memberList.push(processedList);
                    }
                });
                setMembers(memberList);
            })
            .catch((error: any) => {
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            })
            .finally(() => {
                setLoadingMembers(false);
            });
    };

    const onShareTemplate = (shareData: any): void => {
        setSharing(true);
        TemplateService.shareTemplate(shareData)
            .then((response: any) => {
                Utils.handleSuccessResponse(response);
                onCloseForm();
            })
            .catch((error: any) => {
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            })
            .finally(() => {
                setSharing(false);
            });
    };

    // UI Actions
    const actionOnShareTemplate = (currentMember: string): void => {
        const member: any = members.find((e) => e.email === currentMember);
        if (member) {
            const requestPayload = {
                shareTo: member.userId,
                templateId: profileState.userInfo.customDashboardTemplateId,
            };
            onShareTemplate(requestPayload);
        }
    };
    const onChangeSharedMember = useCallback((event: any) => {
        const { current: formik } = formikRef;
        if (formik) {
            const { getFieldHelpers } = formik;
            const helper = getFieldHelpers('isSelectedMember');
            const fieldValue = event.target.value;
            helper.setValue(fieldValue);
        }
    }, []);

    // const onChooseMemberSelection = useCallback(
    //     (event: any, inputValue: any): void => {
    //         if (inputValue) {
    //             const currentMember = inputValue.email;
    //             if (members.some((e) => e.email === currentMember)) {
    //                 setSharedMember(true);
    //             } else if (
    //                 typeof currentMember === 'string' &&
    //                 currentMember.length > 0
    //             ) {
    //                 setSharedMember(false);
    //             } else {
    //                 setSharedMember(false);
    //             }
    //         }
    //     },
    //     [members]
    // );
    // const onChooseBlurSelection = useCallback(
    //     (inputValue: any): void => {
    //         if (inputValue) {
    //             const currentMember = inputValue.email;
    //             if (members.some((e) => e.email === currentMember)) {
    //                 setSharedMember(true);
    //             } else if (
    //                 typeof currentMember === 'string' &&
    //                 currentMember.length > 0
    //             ) {
    //                 setSharedMember(false);
    //             } else {
    //                 setSharedMember(false);
    //             }
    //         }
    //     },
    //     [members]
    // );

    // Hooks
    useEffect(() => {
        loadMembersList();
    }, [profileState]);

    const handleEnterFromProgramiticaly = useCallback(() => {
        const { current: submit } = innerSubmitRef;

        if (submit) {
            submit.click();
        }
    }, [innerSubmitRef.current]);

    const [pendingEmailMemberValues, setPendingEmailMemberValues] =
        React.useState<string | null>(null);
    React.useLayoutEffect(() => {
        const { current: formik } = formikRef;
        if (formik) {
            formik.setFieldValue('email', pendingEmailMemberValues);
        }
    }, [pendingEmailMemberValues]);
    // UI Elements
    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={showForm}
            className="share-template-dialog-wrapper"
        >
            <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={{
                    email: '',
                    isSelectedMember: true,
                }}
                //  validationSchema={shareFormScheme}
                validateOnMount
                onSubmit={(formValues) => {
                    actionOnShareTemplate(formValues.email);
                }}
                validate={(values: any) => {
                    const errors = {} as FormikPropsValues;
                    const isMemberExist = members.some(
                        (e) => e.email === values.email
                    );

                    if (!values.email) {
                        errors.email = 'Member Email is required';
                    }
                    if (values.email) {
                        if (
                            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[a-zA-Z]{2,4}$/i.test(
                                values.email
                            )
                        ) {
                            errors.email = 'Invalid email';
                        } else if (!isMemberExist) {
                            errors.email = 'Not a valid member';
                        }
                    }

                    return errors;
                }}
            >
                {({ handleSubmit, isValid }) => (
                    <form
                        autoComplete="off"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        {false && (
                            <AppFormikTextField
                                name="isSelectedMember"
                                inputProps={{
                                    type: 'hidden',
                                    // value: isSharedMember,
                                }}
                                onChange={onChangeSharedMember}
                                className="isSelectedMember"
                            />
                        )}
                        <Box className="justify-space-between align-center">
                            <Typography variant="h3">Share Template</Typography>
                            <IconButton edge="end" onClick={onCloseForm}>
                                <CloseIcon className="close-icon" />
                            </IconButton>
                        </Box>

                        {isLoadingMembers && members.length === 0 && (
                            <Box className="empty-content">
                                <CircularProgress title="progress loading" />
                            </Box>
                        )}

                        {members.length > 0 && (
                            <Box className="py-4">
                                <InputLabel id="member">
                                    Add member *
                                </InputLabel>
                                <AppFormikTextField
                                    fullWidth
                                    name="email"
                                    labelId="member"
                                    className="mt-3"
                                    autoFocus
                                    onChange={(event: any) => {
                                        const { current: formik } = formikRef;

                                        if (formik) {
                                            const { value } =
                                                formik.getFieldMeta('email');

                                            if (
                                                value !== event.target.value &&
                                                typeof value === 'string' &&
                                                value.length === 0
                                            ) {
                                                setPendingEmailMemberValues(
                                                    event.target.value
                                                );
                                            }
                                        }
                                    }}
                                    onEnter={handleEnterFromProgramiticaly}
                                />
                            </Box>
                        )}

                        <DialogActions>
                            <AppSecondaryButton
                                title="Cancel"
                                type="button"
                                onClick={onCloseForm}
                                className="btn-small"
                            />
                            <AppPrimaryButton
                                innerRef={innerSubmitRef}
                                title="Share"
                                type="button"
                                onClick={handleSubmit}
                                className="btn-small"
                                disabled={!isValid}
                                isLoading={isSharing}
                                loadingText="SHARING"
                            />
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </Dialog>
    );
};

const mapStateToProps = (state: any): any => ({
    profileState: state.profileState,
});

export default connect(mapStateToProps)(ShareTemplateFormDialog);
